import type { AxiosError } from "axios";
import Axios from "axios";
import { endpoints } from "../../../endpoints";
import type {
  AttributeInput,
  SupportedAssetAccessibility,
  SupportedAssetType,
} from "../../../types/types.PIM";
import type { NewAssetFormSubmitValues } from "../../../pages/SharedPages/ProductDetailPage/Assets/util";
import {
  accessibilityRequestData,
  getAssetName,
} from "../../../pages/SharedPages/ProductDetailPage/Assets/util";
import type { IAssetPayload } from "../../../pages/SharedPages/ProductDetailPage/Assets/NewAssetForm";
import { getDateTime } from "../../../util/util";

export async function createDocumentAssetFromForm(
  tenantId: string,
  values: NewAssetFormSubmitValues
): Promise<string> {
  const assetType = values.input_asset_type?.value?.toLowerCase() as
    | SupportedAssetType
    | undefined;
  const categoryId = values.input_category_type?.value;

  if (!assetType || !categoryId) {
    throw new Error("Missing required asset type or category");
  }

  const visibility =
    (values.accessibility as SupportedAssetAccessibility | undefined) ??
    "internal";

  const payload: IAssetPayload & {
    visibility: SupportedAssetAccessibility;
  } = {
    name: getAssetName(
      values.asset_source_mode,
      values.asset_name,
      values.external_asset
    ),
    asset_type: assetType,
    asset_mode: values.asset_source_mode,
    asset_host_id: values.asset_host.value,
    category_id: categoryId,
    language: values.input_lang_type?.value,
    visibility,
    document_type: values.input_document_type?.value || null,
    ...(values.external_asset
      ? { fully_external_url: values.external_asset.url }
      : {}),
    ...accessibilityRequestData(
      assetType,
      visibility,
      values.input_customer_ids ?? [],
      values.input_user_ids ?? [],
      values.input_role_ids ?? []
    ),
  };

  if (values.issue_date) {
    payload.issue_date = getDateTime(values.issue_date).dateFullYear;
  }
  if (values.expiration_date) {
    payload.expiration_date = getDateTime(values.expiration_date).dateFullYear;
  }
  if (values.renewal_date) {
    payload.renewal_date = getDateTime(values.renewal_date).dateFullYear;
  }
  if (values.country?.value) {
    payload.country = values.country.value;
  }
  if (values.region?.value) {
    payload.region = values.region.value;
  }

  const response = await Axios.post(
    endpoints.v2_storefronts_id_pim_assets(tenantId),
    payload
  );
  const assetId = response.data?.id ?? response.data?.data?.id;
  if (!assetId) {
    throw new Error("Asset create response missing id");
  }

  if (values.selected_file) {
    const formData = new FormData();
    formData.append("file", values.selected_file);
    await Axios.put(
      endpoints.v2_tenants_id_pim_assets_id(tenantId, assetId),
      formData
    );
  }

  return assetId;
}

export function getDocumentAssetErrorMessage(
  error: unknown,
  fallback: string
): string {
  return (error as AxiosError)?.response?.data?.message ?? fallback;
}

export const isDocumentAttribute = (
  input_type: AttributeInput | null | undefined
) => {
  return input_type && input_type === "document";
};
